import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkle, ShieldCheck, ArrowRight } from "@phosphor-icons/react";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loadCompanies } = useAuth() || {};

  const [status, setStatus] = useState("loading"); // loading | ready | error | done
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invite/verify`, { params: { token } });
        setInvite(data);
        setName(data.name || "");
        setStatus("ready");
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Invite link is invalid");
        setStatus("error");
      }
    })();
  }, [token]);

  const submit = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== password2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post(`/invite/accept`, { token, password, name: name || undefined });
      toast.success("Welcome to LedgerLoom");
      if (loadCompanies) await loadCompanies();
      // Full reload so AuthContext refetches /auth/me with new cookies
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to accept invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "hsl(168 40% 8%)" }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="invite-accept-card">
        <div className="px-6 py-5 flex items-center gap-3" style={{ background: "hsl(111 71% 37%)", color: "white" }}>
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
            <Sparkle size={18} weight="fill" />
          </div>
          <div>
            <div className="text-lg font-bold leading-none">LedgerLoom</div>
            <div className="text-[10px] uppercase tracking-widest mt-1 opacity-80">Precision Bookkeeping</div>
          </div>
        </div>

        <div className="p-6">
          {status === "loading" && (
            <div className="text-center text-muted-foreground py-8" data-testid="invite-loading">
              Verifying invite…
            </div>
          )}

          {status === "error" && (
            <div className="space-y-3 text-center py-4" data-testid="invite-error">
              <div className="text-lg font-semibold text-destructive">Invite unavailable</div>
              <div className="text-sm text-muted-foreground">{error}</div>
              <Link to="/login" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                Go to sign in <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {status === "ready" && invite && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <ShieldCheck size={18} weight="fill" className="text-primary" />
                <span>
                  Setting up account for <b>{invite.email}</b>
                </span>
              </div>
              <div>
                <Label>Your name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  data-testid="invite-name"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  data-testid="invite-password"
                />
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input
                  type="password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  data-testid="invite-password2"
                />
              </div>
              <Button
                className="w-full"
                onClick={submit}
                disabled={busy}
                data-testid="invite-accept-submit"
              >
                {busy ? "Setting up…" : "Set password & sign in"}
              </Button>
              <div className="text-xs text-muted-foreground text-center">
                By continuing you agree to LedgerLoom&rsquo;s Terms &amp; Privacy Policy.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
